import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { BarChart3 } from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { toast } from 'react-toastify';
import scadenzarioApi from '../services/scadenzarioApi';

const StyledCard = styled(Card)(() => ({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden',
}));

const getPeriodLabel = (period, year, fromDate, toDate) => {
  if (period === 'anno') return `Anno intero ${year}`;
  if (period === 'mese') return `Mese corrente (${fromDate} → ${toDate})`;
  if (period === 'settimana') return `Settimana corrente (${fromDate} → ${toDate})`;
  if (period === 'intervallo') return `Intervallo (${fromDate} → ${toDate})`;
  return 'Tutti gli anni';
};

const formatCompactKM = (value) => {
  const numericValue = Number(value || 0);
  const abs = Math.abs(numericValue);
  if (abs >= 1000000) return `${(numericValue / 1000000).toFixed(1).replace('.', ',')}M`;
  if (abs >= 1000) return `${(numericValue / 1000).toFixed(1).replace('.', ',')}K`;
  return numericValue.toLocaleString('it-IT', { maximumFractionDigits: 0 });
};

const formatPercent = (value) =>
  `${Number(value || 0).toLocaleString('it-IT', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;

function ReferentiPianiTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0]?.payload || {};
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1.2,
        borderRadius: 1.2,
        minWidth: 210,
        boxShadow: '0 8px 20px rgba(15, 23, 42, 0.08)',
      }}
    >
      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, mt: 0.6 }}>
        Piani: {Number(point.piani || 0).toLocaleString('it-IT')}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        Clienti: {Number(point.clienti || 0).toLocaleString('it-IT')}
      </Typography>
      <Box sx={{ mt: 0.9, pt: 0.9, borderTop: '1px solid #E2E8F0' }}>
        <Typography variant="body2" sx={{ color: '#475569' }}>
          Rapporto Clienti/Piani: {formatPercent(point.clienti_per_piano_pct)}
        </Typography>
        <Typography variant="body2" sx={{ color: '#475569' }}>
          Delta Clienti vs Piani: {formatPercent(point.delta_clienti_vs_piani_pct)}
        </Typography>
      </Box>
    </Paper>
  );
}

const shortenLabel = (value, max = 10) => {
  const text = String(value || '');
  return text.length > max ? `${text.slice(0, max)}…` : text;
};

export default function ScadenzarioKpiReferentiPiani({ embedded = false }) {
  const currentYear = new Date().getFullYear();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ referenti: [], statiPiano: [] });
  const [range, setRange] = useState({ year: currentYear, fromDate: '', toDate: '' });
  const [totals, setTotals] = useState({ totalePiani: 0, totaleClientiUnivoci: 0 });
  const [filters, setFilters] = useState({
    period: 'tutti',
    year: String(currentYear),
    user_id: 'all',
    stato_piano: 'all',
    from_date: '',
    to_date: '',
  });

  const availableYears = useMemo(
    () => [currentYear - 2, currentYear - 1, currentYear, currentYear + 1],
    [currentYear]
  );

  const referenteOptions = useMemo(
    () => [{ id: 'all', name: 'Tutti' }, ...(meta.referenti || [])],
    [meta.referenti]
  );

  const fetchMeta = useCallback(async () => {
    try {
      const res = await scadenzarioApi.getMeta();
      setMeta({
        referenti: res.data?.referenti || [],
        statiPiano: res.data?.statiPiano || [],
      });
    } catch (_error) {
      toast.error('Errore caricamento filtri Referenti Piani');
    }
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period: filters.period };
      if (filters.period === 'anno') params.year = Number(filters.year);
      if (filters.user_id !== 'all') params.user_id = Number(filters.user_id);
      if (filters.stato_piano !== 'all') params.stato_piano = filters.stato_piano;
      if (filters.period === 'intervallo' && filters.from_date && filters.to_date) {
        params.from_date = filters.from_date;
        params.to_date = filters.to_date;
      }
      const res = await scadenzarioApi.getKpiReferentiPiani(params);
      setRows(res.data?.data || []);
      setRange({
        year: Number(res.data?.year || currentYear),
        fromDate: res.data?.fromDate || '',
        toDate: res.data?.toDate || '',
      });
      setTotals({
        totalePiani: Number(res.data?.totalePiani || 0),
        totaleClientiUnivoci: Number(res.data?.totaleClientiUnivoci || 0),
      });
    } catch (_error) {
      toast.error('Errore caricamento KPI Referenti Piani');
    } finally {
      setLoading(false);
    }
  }, [
    currentYear,
    filters.from_date,
    filters.period,
    filters.stato_piano,
    filters.to_date,
    filters.user_id,
    filters.year,
  ]);

  useEffect(() => {
    fetchMeta();
  }, [fetchMeta]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const chartData = useMemo(
    () =>
      (rows || []).map((row) => ({
        referente: row.referente_name,
        piani: Number(row.piani_count || 0),
        clienti: Number(row.clienti_univoci || 0),
        clienti_per_piano_pct:
          Number(row.piani_count || 0) > 0
            ? (Number(row.clienti_univoci || 0) / Number(row.piani_count || 0)) * 100
            : 0,
        delta_clienti_vs_piani_pct:
          Number(row.piani_count || 0) > 0
            ? ((Number(row.clienti_univoci || 0) - Number(row.piani_count || 0)) /
                Number(row.piani_count || 0)) *
              100
            : 0,
      })),
    [rows]
  );

  const activeFilterLabel = useMemo(
    () => getPeriodLabel(filters.period, range.year, range.fromDate, range.toDate),
    [filters.period, range.fromDate, range.toDate, range.year]
  );

  return (
    <Box sx={{ p: embedded ? 0 : 3 }}>
      {!embedded ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              color: '#4f46e5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <BarChart3 size={20} />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              Referenti Piani
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Piani in carico e clienti univoci gestiti per referente
            </Typography>
          </Box>
        </Box>
      ) : null}

      <StyledCard>
        <Box sx={{ p: 2.5 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} lg={12} sx={{ order: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.1 }}>
                Piani e Clienti per Referente — {activeFilterLabel}
              </Typography>
              <Box sx={{ height: 460, overflowX: 'auto', overflowY: 'hidden' }}>
                {loading ? (
                  <Box
                    sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  >
                    <CircularProgress size={30} />
                  </Box>
                ) : (
                  <Box sx={{ width: Math.max(chartData.length * 78, 860), height: '100%' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={chartData}
                        margin={{ top: 10, right: 12, left: 0, bottom: 10 }}
                        barCategoryGap={18}
                      >
                        <CartesianGrid stroke="#E5E7EB" strokeDasharray="4 4" vertical={false} />
                        <XAxis
                          dataKey="referente"
                          interval={0}
                          height={62}
                          tick={{ fontSize: 12, fill: '#64748B' }}
                          axisLine={{ stroke: '#CBD5E1' }}
                          tickLine={{ stroke: '#CBD5E1' }}
                          tickFormatter={(value) => shortenLabel(value)}
                        />
                        <YAxis
                          tick={{ fontSize: 12, fill: '#64748B' }}
                          axisLine={{ stroke: '#CBD5E1' }}
                          tickLine={{ stroke: '#CBD5E1' }}
                          tickFormatter={(value) => formatCompactKM(value)}
                        />
                        <Tooltip content={<ReferentiPianiTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 13 }} />
                        <Bar
                          dataKey="piani"
                          name="Piani"
                          fill="#3B82F6"
                          radius={[6, 6, 0, 0]}
                          maxBarSize={34}
                        />
                        <Bar
                          dataKey="clienti"
                          name="Clienti"
                          fill="#10B981"
                          radius={[6, 6, 0, 0]}
                          maxBarSize={34}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Box>
              <Box sx={{ mt: 1.5, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Totale piani
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#3B82F6' }}>
                    {totals.totalePiani.toLocaleString('it-IT')}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Totale clienti univoci
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#10B981' }}>
                    {totals.totaleClientiUnivoci.toLocaleString('it-IT')}
                  </Typography>
                </Paper>
              </Box>
            </Grid>

            <Grid item xs={12} lg={12} sx={{ order: 1 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Grid container spacing={1.5} alignItems="center">
                  <Grid item xs={12} md={2}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Periodo</InputLabel>
                      <Select
                        label="Periodo"
                        value={filters.period}
                        onChange={(e) =>
                          setFilters((prev) => ({ ...prev, period: e.target.value }))
                        }
                      >
                        <MenuItem value="tutti">Tutti gli anni</MenuItem>
                        <MenuItem value="anno">Anno intero</MenuItem>
                        <MenuItem value="mese">Mese corrente</MenuItem>
                        <MenuItem value="settimana">Settimana corrente</MenuItem>
                        <MenuItem value="intervallo">Intervallo</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {filters.period === 'anno' ? (
                    <Grid item xs={12} md={1}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Anno</InputLabel>
                        <Select
                          label="Anno"
                          value={filters.year}
                          onChange={(e) =>
                            setFilters((prev) => ({ ...prev, year: e.target.value }))
                          }
                        >
                          {availableYears.map((item) => (
                            <MenuItem key={item} value={String(item)}>
                              {item}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  ) : null}
                  {filters.period === 'intervallo' ? (
                    <>
                      <Grid item xs={12} md={2}>
                        <TextField
                          size="small"
                          type="date"
                          label="Dal"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          value={filters.from_date}
                          onChange={(e) =>
                            setFilters((prev) => ({ ...prev, from_date: e.target.value }))
                          }
                        />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField
                          size="small"
                          type="date"
                          label="Al"
                          InputLabelProps={{ shrink: true }}
                          fullWidth
                          value={filters.to_date}
                          onChange={(e) =>
                            setFilters((prev) => ({ ...prev, to_date: e.target.value }))
                          }
                        />
                      </Grid>
                    </>
                  ) : null}
                  <Grid item xs={12} md={filters.period === 'intervallo' ? 2 : 3}>
                    <Autocomplete
                      size="small"
                      options={referenteOptions}
                      value={
                        referenteOptions.find(
                          (item) => String(item.id) === String(filters.user_id)
                        ) || referenteOptions[0]
                      }
                      getOptionLabel={(option) => option?.name || ''}
                      isOptionEqualToValue={(option, value) =>
                        String(option.id) === String(value.id)
                      }
                      onChange={(_event, value) =>
                        setFilters((prev) => ({
                          ...prev,
                          user_id: value ? String(value.id) : 'all',
                        }))
                      }
                      renderInput={(params) => <TextField {...params} label="Referente" />}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Stato piano</InputLabel>
                      <Select
                        label="Stato piano"
                        value={filters.stato_piano}
                        onChange={(e) =>
                          setFilters((prev) => ({ ...prev, stato_piano: e.target.value }))
                        }
                      >
                        <MenuItem value="all">Tutti gli stati</MenuItem>
                        {(meta.statiPiano || []).map((item) => (
                          <MenuItem key={item} value={item}>
                            {item}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, width: '100%' }}>
                      <Button
                        color="inherit"
                        onClick={() =>
                          setFilters({
                            period: 'tutti',
                            year: String(currentYear),
                            user_id: 'all',
                            stato_piano: 'all',
                            from_date: '',
                            to_date: '',
                          })
                        }
                      >
                        Azzera
                      </Button>
                      <Button variant="contained" color="warning" onClick={fetchData}>
                        Invia
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </StyledCard>
    </Box>
  );
}
